import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paintbrush, RotateCcw, Undo2 } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { RestyleAreaInput, RestyleMaskData } from '../types/quote';

const AREA_COLORS = ['#3b82f6', '#22c55e', '#f97316', '#a855f7', '#ec4899', '#14b8a6'];

export interface MaskArea {
  id: string;
  color: string;
  strokes: Array<Array<{ x: number; y: number }>>;
  asset_template_id?: string;
}

interface MaskEditorProps {
  imageUrl: string;
  placementMode: 'auto' | 'manual';
  areas: MaskArea[];
  activeAreaId: string | null;
  onAreasChange: (areas: MaskArea[]) => void;
  onActiveAreaChange: (id: string | null) => void;
  onExportReady?: (exportMask: () => Promise<{ blob: Blob; maskData: RestyleMaskData }>) => void;
}

function dist(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function drawStroke(
  ctx: CanvasRenderingContext2D,
  points: Array<{ x: number; y: number }>,
  color: string,
  size: number,
  erase: boolean
) {
  if (points.length < 2) return;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = size;
  if (erase) {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.strokeStyle = 'rgba(0,0,0,1)';
  } else {
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.55;
  }
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
  ctx.stroke();
  ctx.restore();
}

function computeAreaCenter(
  strokes: Array<Array<{ x: number; y: number }>>,
  w: number,
  h: number
): { center_x_pct: number; center_y_pct: number; position_label: string } {
  const pts = strokes.flat();
  if (!pts.length) return { center_x_pct: 0.5, center_y_pct: 0.5, position_label: 'CENTER, mid-height of the room' };
  const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
  const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
  const xPct = cx / w;
  const yPct = cy / h;
  const hLabel = xPct < 0.33 ? 'LEFT SIDE' : xPct > 0.66 ? 'RIGHT SIDE' : 'CENTER';
  const vLabel = yPct < 0.33 ? 'upper' : yPct > 0.66 ? 'lower' : 'mid-height';
  return {
    center_x_pct: Math.round(xPct * 100) / 100,
    center_y_pct: Math.round(yPct * 100) / 100,
    position_label: `${hLabel}, ${vLabel} of the room`,
  };
}

export const MaskEditor: React.FC<MaskEditorProps> = ({
  imageUrl,
  placementMode,
  areas,
  activeAreaId,
  onAreasChange,
  onActiveAreaChange,
  onExportReady,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const maskRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [brushSize, setBrushSize] = useState(40);
  const [drawing, setDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState<Array<{ x: number; y: number }>>([]);
  const [history, setHistory] = useState<MaskArea[][]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');

  const activeArea = areas.find(a => a.id === activeAreaId) ?? areas[0];

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const area of areas) {
      for (const stroke of area.strokes) {
        drawStroke(ctx, stroke, area.color, brushSize, false);
      }
    }
    if (currentStroke.length > 1 && activeArea) {
      drawStroke(ctx, currentStroke, activeArea.color, brushSize, false);
    }
  }, [areas, brushSize, currentStroke, activeArea]);

  useEffect(() => {
    if (!imageUrl) {
      setLoadState('error');
      return;
    }
    setLoadState('loading');
    imgRef.current = null;
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      const mask = maskRef.current;
      if (!canvas || !mask) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      mask.width = img.naturalWidth;
      mask.height = img.naturalHeight;
      setLoadState('ready');
    };
    img.onerror = () => setLoadState('error');
    img.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    if (loadState === 'ready') redraw();
  }, [loadState, redraw]);

  useEffect(() => { redraw(); }, [redraw]);

  const pointFromEvent = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const pushHistory = () => setHistory(h => [...h.slice(-20), areas.map(a => ({ ...a, strokes: a.strokes.map(s => [...s]) }))]);

  const finishStroke = (stroke: Array<{ x: number; y: number }>) => {
    if (!activeArea || stroke.length < 2) return;
    const next = areas.map(a =>
      a.id === activeArea.id
        ? { ...a, strokes: [...a.strokes, stroke] }
        : a
    );
    onAreasChange(next);
  };

  const handlePointerDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setDrawing(true);
    setCurrentStroke([pointFromEvent(e)]);
  };

  const handlePointerMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawing) return;
    const pt = pointFromEvent(e);
    setCurrentStroke(prev => {
      const last = prev[prev.length - 1];
      if (last && dist(last, pt) < 2) return prev;
      return [...prev, pt];
    });
  };

  const handlePointerUp = () => {
    if (!drawing) return;
    setDrawing(false);
    pushHistory();
    finishStroke(currentStroke);
    setCurrentStroke([]);
  };

  const addArea = () => {
    const id = crypto.randomUUID();
    const color = AREA_COLORS[areas.length % AREA_COLORS.length];
    onAreasChange([...areas, { id, color, strokes: [] }]);
    onActiveAreaChange(id);
  };

  const clearAll = () => {
    pushHistory();
    onAreasChange(areas.map(a => ({ ...a, strokes: [] })));
  };

  const undo = () => {
    const prev = history[history.length - 1];
    if (!prev) return;
    onAreasChange(prev);
    setHistory(h => h.slice(0, -1));
  };

  const exportMask = useCallback(async (): Promise<{ blob: Blob; maskData: RestyleMaskData }> => {
    const mask = maskRef.current;
    if (!mask?.width || !mask.height) throw new Error('Mask canvas not ready');
    const w = mask.width;
    const h = mask.height;
    const ctx = mask.getContext('2d')!;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = '#fff';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const area of areas) {
      for (const stroke of area.strokes) {
        if (stroke.length < 2) continue;
        ctx.beginPath();
        ctx.lineWidth = brushSize;
        ctx.strokeStyle = '#fff';
        ctx.moveTo(stroke[0].x, stroke[0].y);
        for (let i = 1; i < stroke.length; i++) ctx.lineTo(stroke[i].x, stroke[i].y);
        ctx.stroke();
      }
    }

    const blob = await new Promise<Blob>((resolve, reject) => {
      mask.toBlob(b => (b ? resolve(b) : reject(new Error('Mask export failed'))), 'image/png');
    });

    const areaInputs: RestyleAreaInput[] = areas
      .filter(a => a.strokes.length > 0)
      .map(a => {
        const center = computeAreaCenter(a.strokes, w, h);
        return {
          id: a.id,
          color: a.color,
          asset_template_id: a.asset_template_id,
          ...center,
        };
      });

    return {
      blob,
      maskData: { placement_mode: placementMode, areas: areaInputs },
    };
  }, [areas, brushSize, placementMode]);

  useEffect(() => {
    onExportReady?.(exportMask);
  }, [exportMask, onExportReady]);

  useEffect(() => {
    if (!areas.length) addArea();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="default">
          <Paintbrush className="h-4 w-4 mr-1" /> Paint
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={undo} disabled={!history.length}>
          <Undo2 className="h-4 w-4 mr-1" /> Undo
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={clearAll}>
          <RotateCcw className="h-4 w-4 mr-1" /> Clear
        </Button>
        {placementMode === 'manual' && (
          <Button type="button" size="sm" variant="secondary" onClick={addArea}>+ New Area</Button>
        )}
        <div className="flex items-center gap-2 ml-auto min-w-[160px]">
          <Label className="text-xs whitespace-nowrap">Size {brushSize}px</Label>
          <Input type="range" min={8} max={120} step={4} value={brushSize} onChange={e => setBrushSize(Number(e.target.value))} className="w-24" />
        </div>
      </div>

      {placementMode === 'manual' && (
        <div className="flex flex-wrap gap-2">
          {areas.map(area => (
            <button
              key={area.id}
              type="button"
              className={`px-2 py-1 rounded text-xs border ${activeAreaId === area.id ? 'ring-2 ring-primary' : ''}`}
              style={{ borderColor: area.color }}
              onClick={() => onActiveAreaChange(area.id)}
            >
              <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: area.color }} />
              Area {areas.indexOf(area) + 1}
            </button>
          ))}
        </div>
      )}

      <div className="border rounded-lg overflow-hidden bg-muted min-h-[240px] flex items-center justify-center">
        {loadState === 'loading' && <p className="text-sm text-muted-foreground p-8">Loading room photo…</p>}
        {loadState === 'error' && <p className="text-sm text-destructive p-8">Could not load room photo.</p>}
        <canvas
          ref={canvasRef}
          className={`w-full h-auto max-h-[480px] object-contain touch-none ${loadState === 'ready' ? 'cursor-crosshair' : 'hidden'}`}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
        />
      </div>
      <canvas ref={maskRef} className="hidden" />
    </div>
  );
};

export default MaskEditor;
